import React, { useState } from "react";
import "./Services.css";
import {
  Car,
  Home,
  Briefcase,
  Plane,
  Shield,
  Building,
  ChevronDown,
  ChevronUp,
  CheckCircle,
} from "lucide-react";
import PageSEO from '../components/PageSEO';

function Services() {
  const [expanded, setExpanded] = useState(null);

  const toggle = (index) =>
    setExpanded((prev) => (prev === index ? null : index));

  const services = [
    {
      id: "auto-insurance",
      icon: <Car className="service-icon" />,
      title: "Auto Insurance",
      description:
        "Whether you drive a family sedan, a pickup truck, or a commercial fleet, we find you the right coverage at a competitive rate. As independent brokers, we compare across multiple insurers to get you the best deal — not just whatever one company offers.",
      bullets: [
        "Collision and comprehensive coverage",
        "Third-party liability protection",
        "Unlisted driver protection",
        "Roadside assistance add-ons",
        "New and used vehicle replacement coverage",
      ],
      subcategories: ["Personal", "Commercial"],
    },
    {
      id: "home-insurance",
      icon: <Home className="service-icon" />,
      title: "Home Insurance",
      description:
        "Your home is likely your biggest investment — we make sure it's properly protected. From detached houses to rental suites and mobile homes, we tailor a policy that covers what matters most to you.",
      bullets: [
        "Dwelling and structure coverage",
        "Personal contents and belongings",
        "Additional living expenses if displaced",
        "Liability protection on your property",
        "Optional overland water and sewer backup",
      ],
      subcategories: [
        "Rental Properties",
        "Rooming and Student Housing",
        "Mobile Homes and Travel Trailers",
      ],
    },
    {
      id: "business-insurance",
      icon: <Briefcase className="service-icon" />,
      title: "Business Insurance",
      description:
        "Running a business comes with risks you can't always predict. We work with small businesses, contractors, and established companies across BC to build insurance packages that protect your livelihood.",
      bullets: [
        "General liability and errors & omissions",
        "Commercial auto for company vehicles",
        "Business interruption coverage",
        "Employee theft and fidelity bonds",
        "Package policies for small businesses",
      ],
    },
    {
      id: "travel-insurance",
      icon: <Plane className="service-icon" />,
      title: "Travel Insurance",
      description:
        "Medical emergencies abroad can cost tens of thousands of dollars. Our travel insurance plans keep you protected whether you're visiting family, going on vacation, or welcoming newcomers to Canada.",
      bullets: [
        "Emergency medical and hospitalization",
        "Trip cancellation and interruption",
        "Lost or delayed baggage coverage",
        "24/7 emergency assistance hotline",
        "Multi-trip annual plans available",
      ],
      subcategories: ["Emergency Medical", "Visitors to Canada", "Super Visa"],
    },
    {
      id: "liability-insurance",
      icon: <Shield className="service-icon" />,
      title: "Liability Insurance",
      description:
        "A single lawsuit or liability claim can threaten everything you've built. Liability insurance provides a critical financial safety net for individuals, landlords, and businesses alike.",
      bullets: [
        "Personal liability for homeowners and tenants",
        "Commercial general liability (CGL)",
        "Professional liability / errors & omissions",
        "Directors and officers (D&O) coverage",
        "Umbrella policies for added protection",
      ],
    },
    {
      id: "commercial-property",
      icon: <Building className="service-icon" />,
      title: "Commercial Property",
      description:
        "From a small office to a large warehouse, your business premises and physical assets need proper protection. We help commercial property owners and tenants find the right coverage for their space.",
      bullets: [
        "Building and structure coverage",
        "Contents, inventory, and equipment",
        "Business income and extra expense coverage",
        "Tenant's legal liability",
        "Flood and earthquake endorsements available",
      ],
    },
  ];

  return (
    <>
    <PageSEO page="services" />
    <div className="services-page">
      {/* --- Hero --- */}
      <section className="services-hero">
        <h1>Our Insurance Services</h1>
        <p>
          We offer a wide range of coverage options designed to fit your needs —
          whether personal, family, or business. As independent brokers, we work
          for you — not the insurance companies.
        </p>
      </section>

      {/* --- Intro Strip --- */}
      <section className="services-intro">
        <div className="intro-item">
          <strong>Independent Brokers</strong>
          <span>We compare across multiple insurers to find you the best rate.</span>
        </div>
        <div className="intro-divider" />
        <div className="intro-item">
          <strong>Licensed Professionals</strong>
          <span>All our advisors are fully licensed in British Columbia.</span>
        </div>
        <div className="intro-divider" />
        <div className="intro-item">
          <strong>No Obligation Quotes</strong>
          <span>Get a free quote with zero pressure — ever.</span>
        </div>
      </section>

      {/* --- Services Grid --- */}
      <section className="services-grid-section">
        <div className="services-grid">
          {services.map((service, index) => (
            <div
              id={service.id}
              className={`service-card ${expanded === index ? "expanded" : ""}`}
              key={index}
            >
              {/* Card Header */}
              <div className="card-header">
                <div className="card-icon-wrap">{service.icon}</div>
                <h3>{service.title}</h3>
              </div>

              {/* Description */}
              <p className="card-description">{service.description}</p>

              {/* What's Covered */}
              <div className="card-coverage">
                <p className="coverage-label">What's covered:</p>
                <ul className="coverage-list">
                  {service.bullets.map((b, i) => (
                    <li key={i}>
                      <CheckCircle size={14} className="check-icon" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Subcategories */}
              {service.subcategories && (
                <div className="card-subcategories">
                  <button
                    className="subcategory-toggle"
                    onClick={() => toggle(index)}
                  >
                    <span>Available Plans</span>
                    {expanded === index ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </button>
                  {expanded === index && (
                    <ul className="subcategories">
                      {service.subcategories.map((sub, i) => (
                        <li key={i}>{sub}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* CTA */}
              <a href="/contact" className="card-cta">
                Get a Quote
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* --- Why Use a Broker --- */}
      <section className="broker-section">
        <h2>Why Use an Insurance Broker?</h2>
        <p>
          Unlike going directly to an insurance company, a broker works
          exclusively in your interest. We have access to dozens of insurers and
          can compare policies side-by-side to find you the right coverage at
          the right price.
        </p>
        <div className="broker-grid">
          <div className="broker-card">
            <h4>We Shop For You</h4>
            <p>
              We compare rates from multiple trusted insurers so you don't have
              to make call after call.
            </p>
          </div>
          <div className="broker-card">
            <h4>Expert Advice</h4>
            <p>
              Our licensed brokers explain your options in plain language — no
              jargon, no runaround.
            </p>
          </div>
          <div className="broker-card">
            <h4>Claims Support</h4>
            <p>
              When you need to make a claim, we're in your corner — guiding you
              through every step.
            </p>
          </div>
          <div className="broker-card">
            <h4>No Extra Cost</h4>
            <p>
              Our brokerage services are free to you. We're paid by the insurers,
              not by charging you extra fees.
            </p>
          </div>
        </div>
      </section>

      {/* --- CTA Banner --- */}
      <section className="services-cta">
        <h2>Not Sure What Coverage You Need?</h2>
        <p>
          Talk to one of our brokers — we'll assess your situation and recommend
          the right coverage with no obligation.
        </p>
        <a href="/contact" className="cta-btn">
          Contact Us Today
        </a>
      </section>
    </div>
    </>
  );
}

export default Services;